import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@mui/material";

import { useGetWarehouses } from "../../hooks/warehouses/useGetWarehouses";
import { useDeleteWarehouse } from "../../hooks/warehouses/useDeleteWarehouse";
import useTableActions from "../../hooks/common/useTableActions";

import TableLayout from "../global/Table/TableLayout";
import { WarehousesTableColumns } from "./Table/WarehousesTableColumns";
import WarehousesTableRow from "./Table/WarehousesTableRow";
import FormAddEditWarehouse from "./FormAddEditWarehouse";

const WarehousesTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const { deleteWarehouseMutate, isDeleting } = useDeleteWarehouse();

    const {
        page, perPage, search, order, selectedIds, menuAnchorEl, selectedItem,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleMenuDelete, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: "/warehouses",
        deleteSingle: deleteWarehouseMutate,
    });

    const params = useMemo(() => ({
        page, per_page: perPage,
        search: search || undefined,
        sort_by: "created_at", sort_order: order,
    }), [page, perPage, search, order]);

    const { warehouses, meta, isLoading, error, refetch } = useGetWarehouses(params);

    const openCreate = () => { setEditingItem(null); setDialogOpen(true); };
    const openEdit = () => {
        if (!selectedItem) return;
        setEditingItem(selectedItem);
        setDialogOpen(true);
        handleMenuClose();
    };
    const closeDialog = () => { setDialogOpen(false); setEditingItem(null); };

    const renderRow = (rowParams) => (
        <WarehousesTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    return (
        <>
            <TableLayout
                title={isArabic ? "المستودعات" : "Warehouses"}
                subtitle={isArabic ? "إدارة مستودعات المخزون" : "Manage stock warehouses"}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? "إضافة مستودع" : "Add Warehouse"}
                onAddClick={openCreate}
                isLoading={isLoading}
                error={error}
                items={warehouses}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                columns={WarehousesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={openEdit}
                onMenuDelete={handleMenuDelete}
                isDeletingSingle={isDeleting}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, warehouses)}
                onRowCheckboxClick={handleRowCheckboxClick}
                bulkDeleteDisabled
                emptyLabelAr="لا توجد مستودعات"
                emptyLabelEn="No warehouses"
                refetch={refetch}
            />

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
                    <FormAddEditWarehouse
                        isEdit={!!editingItem}
                        currentData={editingItem}
                        onSubmitSuccess={closeDialog}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default WarehousesTable;
